import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrashAlt,
  faCheck,
  faArrowsRotate,
  faSave,
  faSearch,
  faTimes,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { Button } from "@/components/ui/button/Button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import DatePicker from "@/components/ui/date-picker";
import { Badge } from "@/components/ui/badge/Badge";
import SwitchToggle from "@/components/ui/switch-toggle";
import { apiClient } from "@/services/api";
import { fetchScreenerSearch } from "@/services/api/screener";
import {
  mapScreenerItemToPortfolioProduct,
  portfolioProductResultKey,
  type PortfolioHoldingProduct,
} from "@/services/api/portfolioProductSearch";
import { useNotification } from "@/context/NotificationContext";
import { format } from "date-fns";
import { isAxiosError } from "axios";

interface PortfolioHoldingsProps {
  portfolioId: string;
  onRecalculationTriggered?: () => void;
}

interface HoldingsRecord {
  _id: string;
  portfolio_id: string;
  as_at_date: string;
  reconciliation_type: "MANUAL" | "DRAFT";
  holdings: Holding[];
  total_value?: number;
  holding_input_type?: "weight_based_model" | "weight_based_actual" | "value_based" | "units_based" | "value_product_currency_based";
  holdings_type?: "TARGET";
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

// Input type matching trade blotter pattern
type HoldingInputType = 'weight_percent' | 'units' | 'value_portfolio_currency' | 'value_product_currency';

interface HoldingCalculatedValues {
  units?: number;
  product_currency_value?: number;
  portfolio_currency_value?: number;
  portfolio_weight_percent?: number;
  product_currency?: string;
  portfolio_currency?: string;
  weight?: number;
}

function apiErrorDetail(error: Error, fallback: string): string {
  if (isAxiosError(error) && error.response?.data) {
    const data = error.response.data;
    if (typeof data === "object" && data !== null && "detail" in data) {
      const detail = (data as { detail: string | string[] }).detail;
      return Array.isArray(detail) ? detail.join(", ") : String(detail);
    }
  }
  return error.message || fallback;
}

interface Holding {
  child_universe_id?: string;
  portfolio_reference_id?: string;
  resource_id?: string;
  resource_type?: "product" | "portfolio" | "benchmark";
  weight?: number;
  value_product_currency?: number;
  value?: number;
  units?: number;
  display_name?: string;
  isin?: string;
  currency?: string;
  holding_type?: "PRODUCT" | "PORTFOLIO";
  calculated_amounts?: {
    units?: {
      amount: number;
      price_per_unit: number;
      price_currency: string;
    };
    product_currency?: {
      amount: number;
      currency: string;
    };
    portfolio_currency?: {
      amount: number;
      currency: string;
    };
    weight?: {
      actual_weight: number;
      target_weight?: number;
    };
  };
}

type Product = PortfolioHoldingProduct;

export function PortfolioHoldings({
  portfolioId,
  onRecalculationTriggered,
}: PortfolioHoldingsProps) {
  const { showSuccess, showError } = useNotification();

  // State
  const [holdings, setHoldings] = useState<HoldingsRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [portfolioClassification, setPortfolioClassification] = useState<'model' | 'actual'>('actual');
  const [portfolioCurrency, setPortfolioCurrency] = useState<string>('USD');

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingHoldings, setEditingHoldings] = useState<HoldingsRecord | null>(
    null
  );
  const [deletingHoldings, setDeletingHoldings] =
    useState<HoldingsRecord | null>(null);

  // Form state
  const [formDate, setFormDate] = useState<string>("");
  const [formHoldings, setFormHoldings] = useState<Holding[]>([]);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [isModelPortfolio, setIsModelPortfolio] = useState(false);
  const [isTargetHoldings, setIsTargetHoldings] = useState(false);

  // New unified input state
  const [holdingInputTypes, setHoldingInputTypes] = useState<{ [key: number]: HoldingInputType }>({});
  const [holdingAmounts, setHoldingAmounts] = useState<{ [key: number]: string }>({});
  const [calculatedAmounts, setCalculatedAmounts] = useState<Record<number, HoldingCalculatedValues>>({});

  // Product search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState<"below" | "above">(
    "below"
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedHoldingIndex, setSelectedHoldingIndex] = useState<
    number | null
  >(null);
  const [dropdownCoords, setDropdownCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const fetchHoldings = useCallback(async (pageNum: number = 1) => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/portfolios/${portfolioId}/holdings/all?page=${pageNum}&page_size=20`
      );
      setHoldings(response.data.data);
      setPage(pageNum);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error("Error fetching holdings:", error);
      showError("Failed to load holdings", "Please try again");
    } finally {
      setLoading(false);
    }
  }, [portfolioId, showError]);

  const fetchPortfolioInfo = useCallback(async () => {
    try {
      const response = await apiClient.get(`/portfolios/${portfolioId}`);
      if (response.data) {
        // Get portfolio classification from fundamentals or default based on client
        const portfolioData = response.data;
        const isModelPortfolio = portfolioData.portfolio_classification === 'model' ||
          portfolioData.client_id === 'octopus' ||
          portfolioData.client_id === 'saltus' ||
          portfolioData.client_id === 'ani';
        setPortfolioClassification(isModelPortfolio ? 'model' : 'actual');

        // Get base currency
        if (portfolioData.base_currency) {
          setPortfolioCurrency(portfolioData.base_currency);
        }
      }
    } catch (error) {
      console.error('Error fetching portfolio info:', error);
    }
  }, [portfolioId]);

  // Search permissioned products, benchmarks, and portfolios via screener.
  const searchProducts = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    setSearchResults([]);
    try {
      const screenerResponse = await fetchScreenerSearch(
        query,
        "display_name",
        "asc",
        1,
        25,
        ["product", "benchmark", "portfolio"],
      );

      const screenerProducts = (screenerResponse.items ?? []).map(
        mapScreenerItemToPortfolioProduct,
      );

      setSearchResults(screenerProducts);
    } catch (error) {
      console.error("Error searching holdings:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPortfolioInfo().then(() => fetchHoldings(1));
  }, [fetchPortfolioInfo, fetchHoldings]);

  useEffect(() => {
    if (selectedHoldingIndex !== null) {
      searchInputRef.current?.focus();
    }
  }, [selectedHoldingIndex]);

  const getHoldingMetaDescription = (
    holding: Pick<Holding, "resource_type" | "holding_type" | "isin" | "currency" | "calculated_amounts">,
  ): string | null => {
    const resourceType =
      holding.resource_type ??
      (holding.holding_type === "PORTFOLIO" ? "portfolio" : "product");
    const capitalizedType =
      resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
    const parts = [capitalizedType];
    if (holding.isin) parts.push(holding.isin);
    const currency =
      holding.currency ?? holding.calculated_amounts?.product_currency?.currency;
    if (currency) parts.push(currency);
    return parts.length > 1 ? parts.join(" • ") : null;
  };

  const getProductDescription = (product: Product): string => {
    const meta = getHoldingMetaDescription({
      resource_type: product.resource_type as Holding["resource_type"],
      isin: product.isin,
      currency: product.currency,
    });
    if (meta) {
      return meta;
    }
    const resourceType = product.resource_type || "product";
    return resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchProducts(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchProducts]);

  // Get previous business day
  const getPreviousBusinessDay = () => {
    const today = new Date();
    const prevDay = new Date(today);
    prevDay.setDate(prevDay.getDate() - 1);

    // Skip weekends
    while (prevDay.getDay() === 0 || prevDay.getDay() === 6) {
      prevDay.setDate(prevDay.getDate() - 1);
    }

    return prevDay.toISOString().split("T")[0];
  };

  // Calculate total weight
  const calculateTotalWeight = (holdings: Holding[]) => {
    return holdings.reduce((sum, h) => sum + (h.weight || 0), 0);
  };

  // Detect input type from first holding (same logic as edit/create)
  const detectInputTypeFromHoldings = (holdings: Holding[]): string => {
    if (holdings.length === 0) return "Weight %";

    const firstHolding = holdings[0];
    const calculatedAmounts = firstHolding.calculated_amounts || {};

    // Check if this is a model portfolio (only has weights)
    if (firstHolding.weight && !firstHolding.units && !firstHolding.value && !firstHolding.value_product_currency) {
      return "Model Weights";
    }
    // Check what the first holding actually contains
    else if (firstHolding.units || calculatedAmounts.units?.amount) {
      return "Units";
    }
    else if (firstHolding.value_product_currency || calculatedAmounts.product_currency?.amount) {
      return "Product Currency";
    }
    else if (firstHolding.value || calculatedAmounts.portfolio_currency?.amount) {
      return "Portfolio Currency";
    }
    else if (firstHolding.weight) {
      return "Actual Weights";
    }

    return "Weight %";
  };

  // Handle create new holdings - prepare form without saving
  const handleCreateNew = async () => {
    try {
      // Get the most recent MANUAL holdings to use as template
      const response = await apiClient.get(
        `/portfolios/${portfolioId}/holdings/all?page=1&page_size=1`
      );

      let initialHoldings: Holding[] = [];

      if (response.data.data.length > 0) {
        // Get detailed holdings with display names
        const latestHoldingsId = response.data.data[0]._id;
        const detailedResponse = await apiClient.get(
          `/portfolios/${portfolioId}/holdings/${latestHoldingsId}`
        );

        // Copy holdings structure from latest
        initialHoldings = detailedResponse.data.holdings.map((h: Holding) => ({
          ...h,
          display_name:
            h.display_name ||
            h.child_universe_id ||
            h.portfolio_reference_id ||
            h.resource_id ||
            "",
        }));
      }

      // Open edit modal with cloned data - nothing is saved to database yet
      setEditingHoldings(null); // null indicates new holdings
      setFormDate(getPreviousBusinessDay());
      setFormHoldings(initialHoldings);

      // Initialize new unified state
      const newInputTypes: { [key: number]: HoldingInputType } = {};
      const newAmounts: { [key: number]: string } = {};

      // Detect input type from the FIRST HOLDING of the template (not metadata)
      let templateInputType: HoldingInputType = 'value_portfolio_currency';
      let isTemplateModelPortfolio = false;

      if (initialHoldings.length > 0) {
        const firstHolding = initialHoldings[0];
        const calculatedAmounts = firstHolding.calculated_amounts || {};

        // Check if this is a model portfolio (only has weights)
        if (firstHolding.weight && !firstHolding.units && !firstHolding.value && !firstHolding.value_product_currency) {
          isTemplateModelPortfolio = true;
          templateInputType = 'weight_percent';
        }
        // Check what the first holding actually contains
        else if (firstHolding.units || calculatedAmounts.units?.amount) {
          templateInputType = 'units';
        }
        else if (firstHolding.value_product_currency || calculatedAmounts.product_currency?.amount) {
          templateInputType = 'value_product_currency';
        }
        else if (firstHolding.value || calculatedAmounts.portfolio_currency?.amount) {
          templateInputType = 'value_portfolio_currency';
        }
        else if (firstHolding.weight) {
          templateInputType = 'weight_percent';
        }
      }

      // Determine if this should be treated as a model portfolio
      const shouldTreatAsModel = isTemplateModelPortfolio || portfolioClassification === 'model';
      setIsModelPortfolio(shouldTreatAsModel);

      initialHoldings.forEach((h: Holding, index: number) => {
        // For model portfolios, always use weight percent
        if (shouldTreatAsModel) {
          newInputTypes[index] = 'weight_percent';
          const weight = h.weight !== undefined && h.weight !== null ? h.weight : 0;
          newAmounts[index] = (weight * 100).toFixed(2);
        } else {
          // For actual portfolios, use the detected input type for ALL holdings
          newInputTypes[index] = templateInputType;

          // Get actual values from calculated_amounts if available, fallback to raw values
          const calculatedAmounts = h.calculated_amounts || {};

          switch (templateInputType) {
                            case 'units': {
                              const units = calculatedAmounts.units?.amount || h.units || 0;
                              newAmounts[index] = units.toString();
                              break;
                            }
                            case 'value_product_currency': {
                              const productValue = calculatedAmounts.product_currency?.amount || h.value_product_currency || 0;
                              newAmounts[index] = productValue.toString();
                              break;
                            }
                            case 'value_portfolio_currency': {
                              const portfolioValue = calculatedAmounts.portfolio_currency?.amount || h.value || 0;
                              newAmounts[index] = portfolioValue.toString();
                              break;
                            }
                            case 'weight_percent': {
                              const weight = calculatedAmounts.weight?.actual_weight || h.weight || 0;
                              newAmounts[index] = (weight * 100).toFixed(2);
                              break;
                            }
                            default:
                              newAmounts[index] = '0';
                          }
        }
      });

      setHoldingInputTypes(newInputTypes);
      setHoldingAmounts(newAmounts);
      setSaveAsDraft(false); // Default to live
      setIsTargetHoldings(false); // Default to actual holdings
      setShowEditModal(true);
    } catch {
      // If error getting template, just open with empty holdings
      setEditingHoldings(null);
      setFormDate(getPreviousBusinessDay());
      setFormHoldings([]);
      setHoldingInputTypes({});
      setHoldingAmounts({});
      setSaveAsDraft(false);
      setIsModelPortfolio(portfolioClassification === 'model');
      setIsTargetHoldings(false);
      setShowEditModal(true);
    }
  };

  // Handle edit
  const handleEdit = async (holdings: HoldingsRecord) => {
    setEditingId(holdings._id);
    try {
      // Ensure portfolio info is loaded first
      if (portfolioClassification === 'actual' && holdings.holding_input_type === 'weight_based_model') {
        await fetchPortfolioInfo();
      }

      // Fetch the detailed holdings with display names
      const response = await apiClient.get(
        `/portfolios/${portfolioId}/holdings/${holdings._id}`
      );
      const detailedHoldings = response.data;

      setEditingHoldings(detailedHoldings);
      setFormDate(detailedHoldings.as_at_date.split("T")[0]);

      // Ensure holdings have display_name populated
      const holdingsWithDisplay = detailedHoldings.holdings.map((h: Holding) => ({
        ...h,
        display_name:
          h.display_name ||
          h.child_universe_id ||
          h.portfolio_reference_id ||
          h.resource_id ||
          "",
      }));
      setFormHoldings(holdingsWithDisplay);

      // Initialize new unified state based on existing data
      const newInputTypes: { [key: number]: HoldingInputType } = {};
      const newAmounts: { [key: number]: string } = {};

      // Detect input type from the FIRST HOLDING (not metadata)
      let detectedInputType: HoldingInputType = 'value_portfolio_currency';
      let isModelHoldings = false;

      if (holdingsWithDisplay.length > 0) {
        const firstHolding = holdingsWithDisplay[0];
        const calculatedAmounts = firstHolding.calculated_amounts || {};

        // Check if this is a model portfolio (only has weights)
        if (firstHolding.weight && !firstHolding.units && !firstHolding.value && !firstHolding.value_product_currency) {
          isModelHoldings = true;
          detectedInputType = 'weight_percent';
        }
        // Check what the first holding actually contains
        else if (firstHolding.units || calculatedAmounts.units?.amount) {
          detectedInputType = 'units';
        }
        else if (firstHolding.value_product_currency || calculatedAmounts.product_currency?.amount) {
          detectedInputType = 'value_product_currency';
        }
        else if (firstHolding.value || calculatedAmounts.portfolio_currency?.amount) {
          detectedInputType = 'value_portfolio_currency';
        }
        else if (firstHolding.weight) {
          detectedInputType = 'weight_percent';
        }
      }

      setIsModelPortfolio(isModelHoldings || portfolioClassification === 'model');

      holdingsWithDisplay.forEach((h: Holding, index: number) => {
        // For model portfolios, always use weight percent
        if (isModelHoldings || portfolioClassification === 'model') {
          newInputTypes[index] = 'weight_percent';
          const weight = h.weight !== undefined && h.weight !== null ? h.weight : 0;
          newAmounts[index] = (weight * 100).toFixed(2);
        } else {
          // For actual portfolios, use the detected input type for ALL holdings
          newInputTypes[index] = detectedInputType;

          // Get actual values from calculated_amounts if available, fallback to raw values
          const calculatedAmounts = h.calculated_amounts || {};

                  switch (detectedInputType) {
                    case 'units': {
                      const units = calculatedAmounts.units?.amount || h.units || 0;
                      newAmounts[index] = units.toString();
                      break;
                    }
                    case 'value_product_currency': {
                      const productValue = calculatedAmounts.product_currency?.amount || h.value_product_currency || 0;
                      newAmounts[index] = productValue.toString();
                      break;
                    }
                    case 'value_portfolio_currency': {
                      const portfolioValue = calculatedAmounts.portfolio_currency?.amount || h.value || 0;
                      newAmounts[index] = portfolioValue.toString();
                      break;
                    }
                    case 'weight_percent': {
                      const weight = calculatedAmounts.weight?.actual_weight || h.weight || 0;
                      newAmounts[index] = (weight * 100).toFixed(2);
                      break;
                    }
                    default:
                      newAmounts[index] = '0';
                  }
        }
      });

      setHoldingInputTypes(newInputTypes);
      setHoldingAmounts(newAmounts);
      setSaveAsDraft(detailedHoldings.reconciliation_type === "DRAFT");
      setIsTargetHoldings(detailedHoldings.holdings_type === "TARGET");
      setShowEditModal(true);

      // Trigger calculations for all holdings after a short delay to ensure state is set
      if (!isModelHoldings && portfolioClassification !== 'model') {
        setTimeout(() => {
          holdingsWithDisplay.forEach((h: Holding, index: number) => {
            const amount = parseFloat(newAmounts[index] || '0');
            if (amount > 0 && h.resource_id) {
              performCalculation(index, amount, newInputTypes[index], h);
            }
          });
        }, 100);
      }
    } catch {
      showError("Failed to load holdings details", "Please try again");
    } finally {
      setEditingId(null);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deletingHoldings) return;

    try {
      await apiClient.delete(
        `/portfolios/${portfolioId}/holdings/${deletingHoldings._id}?skip_refresh=${!!onRecalculationTriggered}`
      );
      showSuccess("Holdings deleted successfully");
      onRecalculationTriggered?.();
      setShowDeleteConfirm(false);
      await fetchHoldings(page);
    } catch {
      showError("Failed to delete holdings", "Please try again");
    }
  };

  // Handle convert draft to manual
  const handleConvertDraft = async (
    holdings: HoldingsRecord,
    recalculate: boolean
  ) => {
    try {
      await apiClient.post(
        `/portfolios/${portfolioId}/holdings/draft/convert/${holdings._id}?skip_refresh=${!!onRecalculationTriggered}`,
        { recalculate }
      );
      showSuccess("Draft holdings converted to live");
      if (recalculate) onRecalculationTriggered?.();
      await fetchHoldings(page);
    } catch {
      showError("Failed to convert draft holdings", "Please try again");
    }
  };

  // Handle save holdings
  const handleSaveHoldings = async () => {
    try {
      // Convert input amounts to proper holding fields based on input type
      const formattedHoldings = formHoldings.map((h, index) => {
        const amount = parseFloat(holdingAmounts[index] || '0');
        const inputType = holdingInputTypes[index] || 'weight_percent';

        const baseHolding = {
          child_universe_id: h.child_universe_id,
          portfolio_reference_id: h.portfolio_reference_id,
          resource_id: h.resource_id,
          resource_type: h.resource_type || "product",
        };

        // Add the appropriate field based on input type
        switch (inputType) {
          case 'weight_percent':
            return { ...baseHolding, weight: amount / 100 };
          case 'units':
            return { ...baseHolding, units: amount };
          case 'value_portfolio_currency':
            return { ...baseHolding, value: amount };
          case 'value_product_currency':
            return { ...baseHolding, value_product_currency: amount };
          default:
            return { ...baseHolding, weight: 0 };
        }
      });

      // Validate holdings
      const validation = await validateHoldings(formattedHoldings);
      if (!validation.valid) {
        showError(
          "Cannot save holdings",
          validation.errors.length > 0
            ? validation.errors.join(". ")
            : "Please check your holdings and try again"
        );
        return;
      }

      const skipRefresh = !!onRecalculationTriggered;

      if (editingHoldings) {
        // Update existing - pass save_as_draft and holdings_type as query params
        const queryParams = new URLSearchParams({
          skip_refresh: String(skipRefresh),
          save_as_draft: String(saveAsDraft),
          holdings_type: isTargetHoldings ? "TARGET" : "",
          as_at_date: formDate + "T00:00:00Z",
        });
        await apiClient.put(
          `/portfolios/${portfolioId}/holdings/${editingHoldings._id}?${queryParams}`,
          formattedHoldings
        );
        const typeLabel = isTargetHoldings ? "target " : "";
        const statusLabel = saveAsDraft ? "draft " : "";
        showSuccess(`${typeLabel}${statusLabel}holdings updated successfully`.replace(/^\w/, c => c.toUpperCase()));
        if (!saveAsDraft && !isTargetHoldings) onRecalculationTriggered?.();
      } else {
        // Create new holdings
        if (formattedHoldings.length === 0) {
          showError("Cannot save empty holdings", "Please add at least one holding");
          return;
        }

        // Determine holding_input_type based on most common input type
        const inputTypes = Object.values(holdingInputTypes);
        let holdingInputType = 'weight_based_model';
        if (portfolioClassification === 'actual') {
          if (inputTypes.includes('units')) {
            holdingInputType = 'units_based';
          } else if (inputTypes.includes('value_product_currency')) {
            holdingInputType = 'value_product_currency_based';
          } else if (inputTypes.includes('value_portfolio_currency')) {
            holdingInputType = 'value_based';
          } else {
            holdingInputType = 'weight_based_actual';
          }
        }

        await apiClient.post(
          `/portfolios/${portfolioId}/holdings/create?skip_refresh=${skipRefresh}`,
          {
            as_at_date: formDate + "T00:00:00Z",
            holdings: formattedHoldings,
            save_as_draft: saveAsDraft,
            holding_input_type: holdingInputType,
            holdings_type: isTargetHoldings ? "TARGET" : undefined,
          }
        );

        const typeLabel = isTargetHoldings ? "target " : "";
        showSuccess(`New ${typeLabel}holdings created successfully`);
        if (!saveAsDraft) onRecalculationTriggered?.();
      }

      setShowEditModal(false);
      await fetchHoldings(1);
    } catch (error) {
      console.error("Save holdings error:", error);
      const errorMessage =
        error instanceof Error
          ? apiErrorDetail(error, "Please try again")
          : "Please try again";
      showError("Failed to save holdings", errorMessage);
    }
  };

  // Validate holdings
  const validateHoldings = async (holdings: Holding[]) => {
    try {
      const response = await apiClient.post(
        `/portfolios/${portfolioId}/holdings/validate`,
        holdings
      );
      return response.data;
    } catch (error) {
      console.error("Validation error:", error);
      const errorMessage =
        error instanceof Error
          ? apiErrorDetail(error, "Validation request failed")
          : "Validation request failed";
      return { valid: false, errors: [errorMessage] };
    }
  };

  // Add holding
  const handleAddHolding = () => {
    const newIndex = formHoldings.length;
    setFormHoldings([
      ...formHoldings,
      {
        weight: 0,
        display_name: "",
        holding_type: "PRODUCT",
        resource_type: "product"
      },
    ]);
    // Initialize input type and amount
    setHoldingInputTypes(prev => ({
      ...prev,
      [newIndex]: isModelPortfolio ? 'weight_percent' : (Object.values(prev)[0] || 'value_portfolio_currency')
    }));
    setHoldingAmounts(prev => ({ ...prev, [newIndex]: isModelPortfolio ? "0.00" : "0" }));
  };

  // Remove holding
  const handleRemoveHolding = (index: number) => {
    setFormHoldings(formHoldings.filter((_, i) => i !== index));

    // Clean up all state by recreating objects with updated indices
    const newInputTypes: { [key: number]: HoldingInputType } = {};
    const newAmounts: { [key: number]: string } = {};
    const newCalculated: Record<number, HoldingCalculatedValues> = {};

    Object.entries(holdingInputTypes).forEach(([key, value]) => {
      const keyIndex = parseInt(key);
      if (keyIndex < index) {
        newInputTypes[keyIndex] = value;
        newAmounts[keyIndex] = holdingAmounts[keyIndex];
        newCalculated[keyIndex] = calculatedAmounts[keyIndex];
      } else if (keyIndex > index) {
        newInputTypes[keyIndex - 1] = value;
        newAmounts[keyIndex - 1] = holdingAmounts[keyIndex];
        newCalculated[keyIndex - 1] = calculatedAmounts[keyIndex];
      }
    });

    setHoldingInputTypes(newInputTypes);
    setHoldingAmounts(newAmounts);
    setCalculatedAmounts(newCalculated);
  };

  // Update holding
  const handleUpdateHolding = <K extends keyof Holding>(
    index: number,
    field: K,
    value: Holding[K],
  ) => {
    const updated = [...formHoldings];
    updated[index] = { ...updated[index], [field]: value };
    setFormHoldings(updated);
  };

  // Handle amount input change
  const handleAmountChange = (index: number, value: string) => {
    setHoldingAmounts(prev => ({ ...prev, [index]: value }));

    // Only perform calculation for actual portfolios
    if (!isModelPortfolio) {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue) && numericValue > 0) {
        performCalculation(index, numericValue, holdingInputTypes[index]);
        // Also recalculate weights for all other holdings
        recalculateAllWeights(index, numericValue);
      }
    }

    // For model portfolios, update weight directly
    if (isModelPortfolio) {
      const weightValue = parseFloat(value);
      if (!isNaN(weightValue)) {
        handleUpdateHolding(index, "weight", weightValue / 100);
      }
    }
  };

  // Recalculate weights for all holdings when one changes
  const recalculateAllWeights = async (changedIndex: number, newValue: number) => {
    // Get all current portfolio values
    const portfolioValues = await Promise.all(
      formHoldings.map(async (holding, index) => {
        if (index === changedIndex) {
          // Use the new value being typed
          const inputType = holdingInputTypes[index];
          if (inputType === 'value_portfolio_currency') {
            return newValue;
          } else {
            // Convert other input types to portfolio currency value
            try {
              const response = await apiClient.post('/portfolios/holdings/calculate', {
                portfolio_id: portfolioId,
                resource_id: holding.resource_id,
                amount: newValue,
                input_type: inputType,
                as_at_date: formDate
              });
              return response.data.calculated_values.portfolio_currency_value;
            } catch {
              return 0;
            }
          }
        } else {
          // Use existing calculated or stored value
          return calculatedAmounts[index]?.portfolio_currency_value ||
            holding.calculated_amounts?.portfolio_currency?.amount ||
            holding.value || 0;
        }
      })
    );

    // Calculate new total and weights
    const newTotalValue = portfolioValues.reduce((sum, val) => sum + val, 0);

    // Update calculated amounts with new weights for all holdings
    const newCalculatedAmounts = { ...calculatedAmounts };
    portfolioValues.forEach((value, index) => {
      if (newCalculatedAmounts[index] || index === changedIndex) {
        newCalculatedAmounts[index] = {
          ...newCalculatedAmounts[index],
          portfolio_currency_value: value,
          weight: newTotalValue > 0 ? value / newTotalValue : 0
        };
      }
    });

    setCalculatedAmounts(newCalculatedAmounts);
  };

  // Perform real-time calculation (like trade blotter)
  const performCalculation = async (
    index: number,
    amount: number,
    inputType: HoldingInputType,
    holdingOverride?: Holding,
  ) => {
    const holding = holdingOverride ?? formHoldings[index];
    if (!holding?.resource_id) return;

    try {
      // Call sense-check style API for real-time calculation
      const response = await apiClient.post('/portfolios/holdings/calculate', {
        portfolio_id: portfolioId,
        resource_id: holding.resource_id,
        amount: amount,
        input_type: inputType,
        as_at_date: formDate
      });

      setCalculatedAmounts(prev => ({
        ...prev,
        [index]: response.data.calculated_values
      }));
    } catch (error) {
      console.error('Calculation error:', error);
    }
  };

  const handleSelectProduct = (product: Product) => {
    if (selectedHoldingIndex === null) {
      return;
    }

    const index = selectedHoldingIndex;
    const selectedLabel = product.long_name || product.display_name;

    handleCloseSearch();

    setFormHoldings((current) => {
      const updated = [...current];
      if (product.resource_type === "portfolio") {
        updated[index] = {
          ...updated[index],
          portfolio_reference_id:
            product.portfolio_reference_id || product.resource_id,
          child_universe_id: undefined,
          resource_id: product.resource_id || product.portfolio_reference_id,
          resource_type: "portfolio",
          display_name: selectedLabel,
          holding_type: "PORTFOLIO",
          isin: undefined,
          currency: product.currency,
        };
      } else {
        const nonPortfolioType =
          product.resource_type === "benchmark" ? "benchmark" : "product";
        updated[index] = {
          ...updated[index],
          child_universe_id: product.child_universe_id || product.resource_id,
          portfolio_reference_id: undefined,
          resource_id: product.resource_id || product.child_universe_id,
          resource_type: nonPortfolioType,
          display_name: selectedLabel,
          holding_type: "PRODUCT",
          isin: product.isin,
          currency: product.currency,
        };
      }
      return updated;
    });
  };

  // Normalize weights
  const handleNormalizeWeights = () => {
    const totalWeight = calculateTotalWeight(formHoldings);
    if (totalWeight === 0) return;

    const normalized = formHoldings.map((h) => ({
      ...h,
      weight: (h.weight || 0) / totalWeight,
    }));
    setFormHoldings(normalized);
  };

  const isSaveHoldingsDisabled =
    isModelPortfolio && Math.abs(calculateTotalWeight(formHoldings) - 1) > 0.0001;

  // Handle opening search for a holding
  const handleOpenSearch = (index: number, element: HTMLElement) => {
    setSelectedHoldingIndex(index);
    setSearchTerm("");
    setSearchResults([]);

    // Calculate position for fixed dropdown
    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 300 && spaceAbove > spaceBelow) {
      // Show above
      setDropdownPosition("above");
      setDropdownCoords({
        top: rect.top - 8, // 8px gap
        left: rect.left,
        width: rect.width,
      });
    } else {
      // Show below
      setDropdownPosition("below");
      setDropdownCoords({
        top: rect.bottom + 8, // 8px gap
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Close search dropdown
  const handleCloseSearch = () => {
    setSelectedHoldingIndex(null);
    setSearchTerm("");
    setSearchResults([]);
    setDropdownCoords(null);
  };

  // Handle click outside
  useEffect(() => {
    if (selectedHoldingIndex !== null && dropdownCoords) {
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (
          !target.closest("[data-search-dropdown]") &&
          !target.closest("[data-search-trigger]")
        ) {
          handleCloseSearch();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [selectedHoldingIndex, dropdownCoords]);

  if (loading && holdings.length === 0) {
    return (
      <div className="p-4 text-gray-700 dark:text-gray-300">
        Loading holdings...
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Holdings
        </h2>
        <div className="flex items-center gap-4">
          {/* TODO: Add What-If scenarios toggle when backend is ready */}
          {/* <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600 dark:text-gray-400">Show What-If Scenarios</span>
              <SwitchToggle
                checked={showWhatIfScenarios}
                onChange={setShowWhatIfScenarios}
              />
            </div> */}
          <Button
            plain
            onClick={handleCreateNew}
            className="px-3 text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Create New Holdings
          </Button>
        </div>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Input Mode</TableHeader>
            <TableHeader>Holdings Count</TableHeader>
            <TableHeader>Total Weight</TableHeader>
            <TableHeader>Created By</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {holdings.map((record) => (
            <TableRow key={record._id}>
              <TableCell>
                {format(new Date(record.as_at_date), "yyyy-MM-dd")}
              </TableCell>
              <TableCell>
                <div className="flex gap-1.5 flex-wrap">
                  {record.holdings_type === "TARGET" ? (
                    <Badge color="purple">Target</Badge>
                  ) : (
                    <Badge color="blue">Actual</Badge>
                  )}
                  {record.reconciliation_type === "DRAFT" ? (
                    <Badge color="yellow">Draft</Badge>
                  ) : (
                    <Badge color="green">Live</Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {detectInputTypeFromHoldings(record.holdings)}
                </span>
              </TableCell>
              <TableCell>{record.holdings.length}</TableCell>
              <TableCell>
                {(calculateTotalWeight(record.holdings) * 100).toFixed(2)}%
              </TableCell>
              <TableCell>{record.created_by}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  {record.reconciliation_type === "DRAFT" && (
                    <>
                      <Button
                        plain
                        onClick={() => handleConvertDraft(record, false)}
                        title="Convert to Live"
                        className="p-2 text-green-600 hover:text-green-700"
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </Button>
                      <Button
                        plain
                        onClick={() => handleConvertDraft(record, true)}
                        title="Convert and Recalculate"
                        className="p-2 text-blue-600 hover:text-blue-700"
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} />
                      </Button>
                    </>
                  )}
                  <Button
                    plain
                    onClick={() => handleEdit(record)}
                    className="p-2"
                    disabled={editingId === record._id}
                  >
                    {editingId === record._id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                    ) : (
                      <FontAwesomeIcon icon={faEdit} />
                    )}
                  </Button>
                  <Button
                    plain
                    onClick={() => {
                      setDeletingHoldings(record);
                      setShowDeleteConfirm(true);
                    }}
                    className="p-2 text-red-600 hover:text-red-700"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4 gap-2">
          <Button
            plain
            onClick={() => fetchHoldings(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-3 py-1"
          >
            Previous
          </Button>
          <span className="px-3 py-1">
            Page {page} of {totalPages}
          </span>
          <Button
            plain
            onClick={() => fetchHoldings(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-3 py-1"
          >
            Next
          </Button>
        </div>
      )}

      {/* Edit Holdings Modal */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="5xl"
      >
        <DialogTitle>
          <div className="flex items-center justify-between">
            <span>{editingHoldings ? "Edit Holdings" : "New Holdings"}</span>
            <div className="flex items-center gap-4">
              {/* Input Mode Selector - only for actual portfolios */}
              {!isModelPortfolio && (
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-gray-600 dark:text-gray-400">Input Mode:</label>
                  <select
                    value={Object.values(holdingInputTypes)[0] || 'value_portfolio_currency'}
                    onChange={(e) => {
                      const newInputType = e.target.value as HoldingInputType;
                      // Apply to all holdings
                      const newTypes: { [key: number]: HoldingInputType } = {};
                      const newAmounts: { [key: number]: string } = {};

                      formHoldings.forEach((h, index) => {
                        newTypes[index] = newInputType;

                        // Update amounts based on calculated_amounts if available
                        const calculatedAmounts = h.calculated_amounts || {};

                        switch (newInputType) {
                          case 'units': {
                            const units = calculatedAmounts.units?.amount || h.units || 0;
                            newAmounts[index] = units.toString();
                            break;
                          }
                          case 'value_product_currency': {
                            const productValue = calculatedAmounts.product_currency?.amount || h.value_product_currency || 0;
                            newAmounts[index] = productValue.toString();
                            break;
                          }
                          case 'value_portfolio_currency': {
                            const portfolioValue = calculatedAmounts.portfolio_currency?.amount || h.value || 0;
                            newAmounts[index] = portfolioValue.toString();
                            break;
                          }
                          case 'weight_percent': {
                            const weight = calculatedAmounts.weight?.actual_weight || h.weight || 0;
                            newAmounts[index] = (weight * 100).toFixed(2);
                            break;
                          }
                          default:
                            newAmounts[index] = holdingAmounts[index] || '0';
                        }
                      });

                      setHoldingInputTypes(newTypes);
                      setHoldingAmounts(newAmounts);
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="units">Units</option>
                    <option value="value_portfolio_currency">Portfolio Currency</option>
                    <option value="value_product_currency">Product Currency</option>
                  </select>
                </div>
              )}

              {/* Actual/Target Toggle */}
              <div className="flex items-center gap-2 text-sm border-r border-gray-300 dark:border-gray-600 pr-4">
                <span
                  className={`transition-all duration-200 ${!isTargetHoldings
                    ? "font-semibold text-blue-600 dark:text-blue-400"
                    : "text-gray-400"
                    }`}
                >
                  {!isTargetHoldings && (
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-1"></span>
                  )}
                  Actual
                </span>
                <SwitchToggle checked={isTargetHoldings} onChange={setIsTargetHoldings} />
                <span
                  className={`transition-all duration-200 ${isTargetHoldings
                    ? "font-semibold text-purple-600 dark:text-purple-400"
                    : "text-gray-400"
                    }`}
                >
                  {isTargetHoldings && (
                    <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-1"></span>
                  )}
                  Target
                </span>
              </div>

              {/* Draft/Live Toggle */}
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`transition-all duration-200 ${!saveAsDraft
                    ? "font-semibold text-green-600 dark:text-green-400"
                    : "text-gray-400"
                    }`}
                >
                  {!saveAsDraft && (
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                  )}
                  Live
                </span>
                <SwitchToggle checked={saveAsDraft} onChange={setSaveAsDraft} />
                <span
                  className={`transition-all duration-200 ${saveAsDraft
                    ? "font-semibold text-amber-600 dark:text-amber-400"
                    : "text-gray-400"
                    }`}
                >
                  {saveAsDraft && (
                    <span className="inline-block w-2 h-2 bg-amber-500 rounded-full mr-1"></span>
                  )}
                  Draft
                </span>
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogBody>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date
              </label>
              <DatePicker
                mode="single"
                value={formDate}
                onChange={(date) => setFormDate(date as string)}
                disableFutureDates={!saveAsDraft}
                placeholder="Select date"
                showPresets={false}
                className="w-full"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Holdings
                </label>
                {isModelPortfolio && (
                  <div className="flex items-center gap-4 text-sm">
                    <span
                      className={`font-medium ${Math.abs(calculateTotalWeight(formHoldings) - 1) > 0.0001
                        ? "text-red-600"
                        : "text-green-600"
                        }`}
                    >
                      Total:{" "}
                      {(calculateTotalWeight(formHoldings) * 100).toFixed(2)}%
                    </span>
                    {Math.abs(calculateTotalWeight(formHoldings) - 1) >
                      0.0001 && (
                        <button
                          onClick={handleNormalizeWeights}
                          className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                        >
                          Normalize
                        </button>
                      )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg overflow-hidden">
                <div className="max-h-[500px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                          Holding
                        </th>
                        <th className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 w-32">
                          {isModelPortfolio ? 'Weight %' : 'Amount'}
                        </th>
                        {!isModelPortfolio && (
                          <th className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                            Portfolio Value & Weight
                          </th>
                        )}
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {formHoldings.map((holding, index) => {
                        const holdingMeta = getHoldingMetaDescription(holding);
                        return (
                        <tr
                          key={index}
                          data-holding-index={index}
                          className="hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <td className="py-2 px-3">
                            {selectedHoldingIndex === index ? (
                              <div
                                className="flex items-center gap-2"
                                data-search-trigger
                              >
                                <FontAwesomeIcon
                                  icon={faSearch}
                                  className="text-gray-400 text-sm"
                                />
                                <input
                                  ref={searchInputRef}
                                  type="text"
                                  placeholder="Search products or portfolios..."
                                  className="flex-1 bg-transparent border-b border-indigo-500 px-1 py-0.5 text-sm focus:outline-none"
                                  value={searchTerm}
                                  onChange={(e) =>
                                    setSearchTerm(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      handleCloseSearch();
                                    }
                                  }}
                                />
                                {searchTerm && searchResults.length > 0 && (
                                  <span className="text-xs text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                                    {searchResults.length} found
                                  </span>
                                )}
                                <button
                                  onClick={handleCloseSearch}
                                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="text-xs"
                                  />
                                </button>
                              </div>
                            ) : (
                              <div
                                role="button"
                                tabIndex={0}
                                className="group cursor-pointer"
                                onClick={(e) => {
                                  handleOpenSearch(index, e.currentTarget);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleOpenSearch(index, e.currentTarget);
                                  }
                                }}
                                data-search-trigger
                              >
                                <div className="flex flex-col gap-1 min-w-0">
                                  <span
                                    className={`text-sm group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors ${holding.display_name
                                      ? ""
                                      : "text-gray-400 italic"
                                      }`}
                                  >
                                    {holding.display_name ||
                                      "Click to select holding"}
                                  </span>
                                  {holdingMeta && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {holdingMeta}
                                    </span>
                                  )}
                                  {(holding.resource_type === "portfolio" || holding.holding_type === "PORTFOLIO") && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                                      Portfolio
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>

                          {/* Amount Input */}
                          <td className="py-2 px-3">
                            <input
                              type="text"
                              inputMode="decimal"
                              className="w-full px-2 py-1 text-sm text-right bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                              value={holdingAmounts[index] || ''}
                              onChange={(e) => handleAmountChange(index, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              placeholder={isModelPortfolio ? '0.00' : '0'}
                            />
                          </td>

                          {/* Calculated Values - only for non-model portfolios */}
                          {!isModelPortfolio && (
                            <td className="py-2 px-3">
                              <div className="text-xs space-y-1">
                                <div className="text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">
                                    {/* Show real-time if available, otherwise stored */}
                                    {calculatedAmounts[index]?.portfolio_currency_value?.toLocaleString() ||
                                      (holding.calculated_amounts?.portfolio_currency?.amount || holding.value || 0).toLocaleString()}
                                  </span>
                                  {calculatedAmounts[index] && (
                                    <span className="text-xs text-green-600 dark:text-green-400 ml-1">●</span>
                                  )}
                                </div>
                                <div className="text-indigo-600 dark:text-indigo-400 font-medium">
                                  {/* Show real-time if available, otherwise stored */}
                                  {(() => {
                                    const calculatedWeight = calculatedAmounts[index]?.weight;
                                    if (calculatedWeight !== undefined) {
                                      return `${(calculatedWeight * 100).toFixed(2)}%`;
                                    }
                                    return `${((holding.calculated_amounts?.weight?.actual_weight || holding.weight || 0) * 100).toFixed(2)}%`;
                                  })()}
                                </div>
                              </div>
                            </td>
                          )}
                          <td className="py-2 px-3">
                            <button
                              onClick={() => handleRemoveHolding(index)}
                              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all"
                            >
                              <FontAwesomeIcon
                                icon={faTrashAlt}
                                className="text-xs"
                              />
                            </button>
                          </td>
                        </tr>
                        );
                      })}

                      {/* Add new holding row */}
                      <tr className="bg-gray-50 dark:bg-gray-900">
                        <td colSpan={isModelPortfolio ? 3 : 4} className="py-2 px-3">
                          <button
                            onClick={handleAddHolding}
                            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            <FontAwesomeIcon
                              icon={faPlus}
                              className="mr-1.5 text-xs"
                            />
                            Add Holding
                          </button>
                        </td>
                      </tr>

                      {/* Total row for actual portfolios - use STORED calculated_amounts */}
                      {!isModelPortfolio && formHoldings.length > 0 && (
                        <tr className="bg-slate-100 dark:bg-slate-800 font-medium">
                          <td className="py-2 px-3 text-sm text-slate-700 dark:text-slate-300">
                            Total
                          </td>
                          <td className="py-2 px-3 text-right text-sm text-slate-700 dark:text-slate-300">
                            -
                          </td>
                          <td className="py-2 px-3 text-left">
                            <div className="text-xs space-y-1">
                              <div className="text-slate-700 dark:text-slate-300">
                                <span className="font-semibold">
                                  {formHoldings.reduce((sum, h, index) => {
                                    // Use live calculated value if available, otherwise stored
                                    const liveValue = calculatedAmounts[index]?.portfolio_currency_value;
                                    const storedValue = h.calculated_amounts?.portfolio_currency?.amount || h.value || 0;
                                    return sum + (liveValue ?? storedValue);
                                  }, 0).toLocaleString()} {portfolioCurrency}
                                </span>
                                {Object.keys(calculatedAmounts).length > 0 && (
                                  <span className="text-xs text-green-600 dark:text-green-400 ml-1">●</span>
                                )}
                              </div>
                              <div className="text-indigo-600 dark:text-indigo-400 font-medium">
                                {formHoldings.reduce((sum, h, index) => {
                                  // Use live calculated weight if available, otherwise stored
                                  const liveWeight = calculatedAmounts[index]?.weight;
                                  const storedWeight = h.calculated_amounts?.weight?.actual_weight || h.weight || 0;
                                  return sum + ((liveWeight ?? storedWeight) * 100);
                                }, 0).toFixed(2)}%
                              </div>
                            </div>
                          </td>
                          <td className="py-2 px-3"></td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} plain>
            Cancel
          </Button>
          <Button
            onClick={handleSaveHoldings}
            color="indigo"
            disabled={isSaveHoldingsDisabled}
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            Save Holdings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        size="sm"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogBody>
          <p>Are you sure you want to delete these holdings?</p>
          {deletingHoldings && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Date:{" "}
              {format(new Date(deletingHoldings.as_at_date), "yyyy-MM-dd")}
              <br />
              Type:{" "}
              {deletingHoldings.reconciliation_type === "DRAFT"
                ? "Draft"
                : "Live"}
            </p>
          )}
        </DialogBody>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} plain>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="red">
            Delete Holdings
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fixed position search dropdown */}
      {selectedHoldingIndex !== null &&
        dropdownCoords &&
        createPortal(
          <div
            data-search-dropdown
            style={{
              position: "fixed",
              top:
                dropdownPosition === "above"
                  ? "auto"
                  : `${dropdownCoords.top}px`,
              bottom:
                dropdownPosition === "above"
                  ? `${window.innerHeight - dropdownCoords.top + 8}px`
                  : "auto",
              left: `${dropdownCoords.left}px`,
              width: `${dropdownCoords.width}px`,
              zIndex: 9999,
            }}
            className="animate-in fade-in-0 zoom-in-95 duration-200"
          >
            {searchTerm && searchLoading && (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm text-gray-500 dark:text-gray-400">
                Searching…
              </div>
            )}
            {searchTerm && searchResults.length === 0 && !searchLoading && (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm text-gray-500 dark:text-gray-400">
                No results found for &ldquo;{searchTerm}&rdquo;
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
                <div className="max-h-60 overflow-y-auto">
                  {searchResults.map((product) => (
                    <button
                      key={portfolioProductResultKey(product)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 focus:bg-gray-50 dark:focus:bg-gray-700 focus:outline-none"
                      onClick={() => handleSelectProduct(product)}
                    >
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {product.long_name || product.display_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {getProductDescription(product)}
                      </div>
                    </button>
                  ))}
                </div>
                {searchResults.length > 10 && (
                  <div className="px-3 py-1 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Showing {Math.min(searchResults.length, 50)} of{" "}
                    {searchResults.length} results
                  </div>
                )}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
